import { useState, useEffect, useCallback } from 'react';

export interface SyncConflict {
  url: string;
  resolution: 'local-wins' | 'server-wins' | 'discarded' | 'failed';
  detail: string;
}

export interface ConflictLogEntry {
  id: number;
  url: string;
  method: string;
  queuedAt: number;
  resolvedAt: number;
  resolution: string;
  detail: string;
}

/**
 * Hook to track online/offline status and listen for sync conflict events
 * from the service worker.
 */
export function useOfflineStatus() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [showConflictLog, setShowConflictLog] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setDismissed(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for conflict messages from the service worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_CONFLICT') {
        setConflicts((prev) => [...prev, event.data.conflict]);
        setShowConflictLog(true);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  const dismissConflicts = useCallback(() => {
    setConflicts([]);
    setShowConflictLog(false);
  }, []);

  const dismissBanner = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    isOffline,
    isBannerVisible: isOffline && !dismissed,
    conflicts,
    showConflictLog,
    setShowConflictLog,
    dismissConflicts,
    dismissBanner,
  };
}

/**
 * Fetch the full conflict log from the service worker's IndexedDB store.
 */
export async function fetchConflictLog(): Promise<ConflictLogEntry[]> {
  const controller = navigator.serviceWorker?.controller;
  if (!controller) return [];

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve(event.data?.conflicts ?? []);
    };
    controller.postMessage(
      { type: 'GET_CONFLICT_LOG' },
      [channel.port2]
    );
    // Timeout after 3s
    setTimeout(() => resolve([]), 3000);
  });
}

/**
 * Clear the conflict log in the service worker.
 */
export function clearConflictLog(): void {
  navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CONFLICT_LOG' });
}

/**
 * Tell the service worker to pre-cache a list of safety document URLs.
 */
export function precacheSafetyDocuments(urls: string[], token?: string | null): void {
  navigator.serviceWorker?.controller?.postMessage({
    type: 'PRECACHE_SAFETY_DOCS',
    urls,
    token: token ?? undefined,
  });
}
